import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnInit,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { _, TranslateService } from "@ngx-translate/core";
import {
  CustomDetailComponent,
  DataService,
  NotificationService,
  SharedModule,
} from "@vendure/admin-ui/core";
import { unique } from "@vendure/common/lib/unique";
import { Facet, FacetValue, Product } from "@vendure/core";
import { combineLatest, Observable } from "rxjs";
import { map, shareReplay, startWith } from "rxjs/operators";
import { GET_REQUIRED_FACETS } from "./queries.graphql";

type RequiredFacetValueStatus = "complete" | "incomplete";

@Component({
  imports: [SharedModule],
  standalone: true,
  selector: "suggested-facets",
  templateUrl: `./suggested-facets.component.html`,
  styleUrls: [`./suggested-facets.component.scss`],
})
export class SuggestedFacetsComponent
  implements CustomDetailComponent, OnInit, AfterViewChecked
{
  detailForm: FormGroup;
  entity$: Observable<Product>;
  status$: Observable<RequiredFacetValueStatus>;
  requiredFacets$: Observable<
    Array<{
      facet: Facet;
      selectedValues: FacetValue[];
    }>
  >;

  constructor(
    private dataService: DataService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private router: Router, // Inject Router
    private notificationService: NotificationService,
  ) {}

  ngOnInit() {
    this.loadData();

    this.translateService.onLangChange.subscribe(() => {
      this.loadData();
      // this.cdRef.detectChanges();
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        window.location.reload();
      }
    });
  }

  ngAfterViewChecked(): void {
    this.cdRef.detectChanges();
  }
  loadData(): void {
    const selectedFacetValueIds$ = this.detailForm
      .get(["facetValueIds"])!
      .valueChanges.pipe(
        startWith(this.detailForm.get(["facetValueIds"])!.value),
      );
    const possiblyRequiredFacets$ = this.dataService
      .query<any, any>(GET_REQUIRED_FACETS, {}, "cache-first")
      .single$.pipe(
        map(({ requiredFacets }) =>
          requiredFacets.filter(
            ({ customFields }) =>
              customFields?.showOnProductDetail === true ||
              customFields?.showOnProductDetailIf?.length,
          ),
        ),
      );
    this.requiredFacets$ = combineLatest([
      possiblyRequiredFacets$,
      selectedFacetValueIds$,
    ]).pipe(
      shareReplay(1),
      map(([facets, selectedFacetValueIds]) => {
        return facets
          .filter(
            ({ customFields }) =>
              customFields?.showOnProductDetail === true ||
              customFields?.showOnProductDetailIf?.find((f) =>
                selectedFacetValueIds.includes(f.id),
              ),
          )
          .map((facet) => ({
            facet,
            selectedValues: facet.values.filter((value) =>
              selectedFacetValueIds.includes(value.id),
            ),
          }));
      }),
    );
    this.status$ = this.requiredFacets$.pipe(
      map((items) =>
        items.every((i) => 0 < i.selectedValues.length)
          ? "complete"
          : "incomplete",
      ),
    );
  }

  addFacetValue(facetValue: FacetValue) {
    const productGroup = this.getProductFormGroup();
    const currentFacetValueIds: string[] = productGroup.value.facetValueIds;
    productGroup.patchValue({
      facetValueIds: unique([...currentFacetValueIds, facetValue.id]),
    });
    productGroup.markAsDirty();
  }

  save() {
    const id = this.activatedRoute.snapshot.params.id;
    this.dataService.product
      .updateProduct({
        id,
        facetValueIds: this.detailForm.get("facetValueIds")!.value,
      })
      .subscribe(
        (result) => {
          this.detailForm.markAsPristine();
          this.notificationService.success(_("common.notify-update-success"), {
            entity: "Product",
          });
          this.cdRef.markForCheck();
        },
        (err) => {
          this.notificationService.error(_("common.notify-update-error"), {
            entity: "Product",
          });
        },
      );
  }

  removeFacetValue(facetValue: FacetValue) {
    const productGroup = this.getProductFormGroup();
    const currentFacetValueIds: string[] = productGroup.value.facetValueIds;
    productGroup.patchValue({
      facetValueIds: unique([
        ...currentFacetValueIds.filter((id) => id !== facetValue.id),
      ]),
    });
    productGroup.markAsDirty();
  }

  private getProductFormGroup(): FormGroup {
    return this.detailForm as FormGroup;
  }
}
